"use client"

import * as React from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/utils/utils"
import { styleDrivers } from "@/utils/componentStyle"

export interface SheetProps {
  /** Trigger element that opens the sheet */
  trigger?: React.ReactNode;
  /** Sheet title */
  title?: string;
  /** Sheet description */
  description?: string;
  /** Side from which the sheet appears */
  side?: "top" | "right" | "bottom" | "left";
  /** Content to render in the sheet body */
  children?: React.ReactNode;
  /** Footer content (e.g., action buttons) */
  footer?: React.ReactNode;
  /** Whether to show the close button */
  showClose?: boolean;
  /** Controlled open state */
  open?: boolean;
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Additional class name for the content */
  className?: string;
}

const Sheet: React.FC<SheetProps> = ({
  trigger,
  title,
  description,
  side = "right",
  children,
  footer,
  showClose = true,
  open,
  onOpenChange,
  className,
}) => {
  return (
    <SheetPrimitive.Root open={open} onOpenChange={onOpenChange}>
      {trigger && (
        <SheetPrimitive.Trigger asChild>
          {trigger}
        </SheetPrimitive.Trigger>
      )}
      <SheetPrimitive.Portal>
        <SheetPrimitive.Overlay className={styleDrivers.sheet.overlay} />
        <SheetPrimitive.Content
          className={cn(
            styleDrivers.sheet.contentBase,
            styleDrivers.sheet.contentSides[side],
            className
          )}
        >
          <div className={styleDrivers.sheet.content}>
            {/* Header */}
            {(title || description || showClose) && (
              <div className={styleDrivers.sheet.header}>
                <div className="flex-1">
                  {title && (
                    <SheetPrimitive.Title className={styleDrivers.sheet.title}>
                      {title}
                    </SheetPrimitive.Title>
                  )}
                  {description && (
                    <SheetPrimitive.Description className={styleDrivers.sheet.description}>
                      {description}
                    </SheetPrimitive.Description>
                  )}
                </div>
                {showClose && (
                  <SheetPrimitive.Close className={styleDrivers.sheet.close}>
                    <X className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                  </SheetPrimitive.Close>
                )}
              </div>
            )}

            {/* Body */}
            {children && (
              <div className="flex-1 overflow-auto p-4">
                {children}
              </div>
            )}

            {/* Footer */}
            {footer && (
              <div className={styleDrivers.sheet.footer}>
                {footer}
              </div>
            )}
          </div>
        </SheetPrimitive.Content>
      </SheetPrimitive.Portal>
    </SheetPrimitive.Root>
  );
};

export { Sheet };
export default Sheet;
